// src/utils/defectStatus.js — v0.14.0, On hold + Cancelled v0.312.0
// ---------------------------------------------------------------------
// Defect status helpers.
//
// Lifecycle:
//   open       Defect raised. Pin sits on the plan, item lives on the
//              register. Default starting state.
//   on_hold    v0.312.0 — parked: still to be done, waiting on someone or
//              something. Counts as OUTSTANDING (not done), shown apart
//              from Open so a held item is not chased as if it were live.
//   rectified  Supervisor / contractor has done the fix. v0.15.0 will
//              also require a photo + comment as evidence; today, the
//              admin can flip the bit without evidence.
//   complete   Owner of the report has confirmed the rectification is
//              acceptable. Terminal state. v0.16.0 adds the owner-side
//              "Confirm complete" sign-off UI from public share links.
//   cancelled  v0.312.0 — no longer needed. Kept for the record but OUT of
//              the count: not in the total, not in % done, never offered
//              to a subcontractor or client to rectify.
//   Brett 2026-11-03: "we need on hold and cancelled".
//
// Legacy values:
//   in_progress / closed / new — these existed in earlier defect data
//   (InSpec parity) but are folded back on read:
//     in_progress → open
//     closed      → complete    (closed historically meant "done done")
//     new         → open
//   Writes always use the canonical keys so the data converges over time.
//
// The colour palette mirrors the trade pin colours so the visual story
// reads consistently across PlanScreen pins, RegisterScreen rows, the
// PDF defect blocks, and the public share view.

export const STATUSES = ['open', 'on_hold', 'rectified', 'complete', 'cancelled'];

export const STATUS_LABEL = {
  open:      'Open',
  on_hold:   'On hold',
  rectified: 'Rectified',
  complete:  'Complete',
  cancelled: 'Cancelled',
};

// Visual colours for pills / pin highlights / progress bars.
//   fg = text colour on a light background
//   bg = light tint behind the text
//   pin = full-saturation colour used for SVG pins and the progress bar
// Hex, not tokens: the PDF and the drawn pins need real colours.
export const STATUS_COLORS = {
  open:      { fg: '#991b1b', bg: '#fee2e2', pin: '#dc2626' },
  on_hold:   { fg: '#5b21b6', bg: '#ede9fe', pin: '#7c3aed' },
  rectified: { fg: '#92400e', bg: '#fef3c7', pin: '#f59e0b' },
  complete:  { fg: '#14532d', bg: '#dcfce7', pin: '#16a34a' },
  cancelled: { fg: '#475569', bg: '#f1f5f9', pin: '#94a3b8' },
};

// Normalise any legacy / unexpected status string to one of the canonical
// keys. Safe to pass null / undefined / mixed-case input. A key missing
// here silently reads as Open everywhere — add every new status.
export function normaliseStatus(s) {
  const k = String(s || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (k === 'rectified') return 'rectified';
  if (k === 'complete' || k === 'closed') return 'complete';
  if (k === 'on_hold' || k === 'onhold' || k === 'hold') return 'on_hold';
  if (k === 'cancelled' || k === 'canceled') return 'cancelled';
  return 'open';  // open / new / in_progress / unknown
}

// Raw value is one of the canonical keys (what the server accepts).
export function isStatusKey(s) {
  return STATUSES.includes(s);
}

// Still to be done: Open or On hold.
export function isOutstanding(s) {
  const k = normaliseStatus(s);
  return k === 'open' || k === 'on_hold';
}

// Work done: Rectified or Complete.
export function isDone(s) {
  const k = normaliseStatus(s);
  return k === 'rectified' || k === 'complete';
}

export function isCancelled(s) {
  return normaliseStatus(s) === 'cancelled';
}

export function statusLabel(s) {
  return STATUS_LABEL[normaliseStatus(s)];
}

export function statusColors(s) {
  return STATUS_COLORS[normaliseStatus(s)];
}

// Aggregate a list of defects into counts per state. `total` leaves
// cancelled items OUT (they are not work); `all` counts every row.
// `done` = rectified + complete; `pct` = done of total, 0 when none.
export function statusCounts(defects) {
  const out = { open: 0, on_hold: 0, rectified: 0, complete: 0, cancelled: 0, total: 0, all: 0, done: 0, pct: 0 };
  for (const d of defects || []) {
    const k = normaliseStatus(d?.status);
    out[k]++;
    out.all++;
    if (k !== 'cancelled') out.total++;
  }
  out.done = out.rectified + out.complete;
  out.pct = out.total > 0 ? Math.round((out.done / out.total) * 100) : 0;
  return out;
}

// The next status in the workflow for an admin click. Open → Rectified
// → Complete → Open (full cycle). On hold and Cancelled are chosen on
// purpose, never reached by cycling; cycling from either goes to Open.
export function nextStatus(s) {
  const cur = normaliseStatus(s);
  if (cur === 'open') return 'rectified';
  if (cur === 'rectified') return 'complete';
  return 'open';
}
